import { Spinner, Center, Flex, Button } from "@chakra-ui/react";
import DocumentsTable from "components/elements/DocumentsTable";
import NoResults from "components/elements/NoResults";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import PageHeader from "components/layout/PageHeader";
import { useContext, useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import UploadDocuments from "./elements/UploadDocuments";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertsContext } from "providers/alerts";
import { permissionConstants, hasPermission } from "utils/permissionHelper";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useQuery } from "@tanstack/react-query";
import { getErrorMessage } from "utils/errorHandler";
import { Tables } from "types/supabase";
import InputSearch from "components/elements/InputSearch/input-search";

function Documents() {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const { showError } = useContext(AlertsContext);
  const { user } = useTenant();
  const [searchQuery, setSearchQuery] = useState("");
  const [downloadFiles, setDownloadFiles] = useState<any[]>([]);
  const [documentDownloading, setDocumentDownloading] = useState(false);
  const [filteredDocuments, setFilteredDocuments] = useState<any[]>([]);

  const { data: documents, isLoading } = useQuery({
    queryKey: ["documents-list", user.id],
    queryFn: () => apiRequest<{ documents: Tables<"documents">[] }>("/supabase/documents/select"),
    enabled: hasPermission(user, permissionConstants.viewAllDocuments),
    select: data => data.documents ?? [],
  });

  useEffect(() => {
    setFilteredDocuments(() => {
      return (
        documents?.filter((document: { name: any }) =>
          `${document.name}`.toLowerCase().includes(searchQuery.toLowerCase())
        ) || []
      );
    });
  }, [searchQuery, documents]);

  const handleSelectAllRows = () => {
    const allIds: string[] = documents?.map((obj: any) => obj.id) ?? [];
    const allRowsSelected = selectedRows.length === allIds.length;
    const updatedSelection = allRowsSelected ? [] : allIds;
    setSelectAllChecked(!allRowsSelected);
    setSelectedRows(updatedSelection);
  };

  const handleRowSelect = (id: string) => {
    setSelectedRows(prevSelectedRows => {
      const updatedSelection = prevSelectedRows.includes(id)
        ? prevSelectedRows.filter(rowId => rowId !== id)
        : [...prevSelectedRows, id];
      setSelectAllChecked(updatedSelection.length === (documents?.length ?? 0));
      return updatedSelection;
    });
  };

  const downloadDocument = async (id: any) => {
    try {
      const response = await apiRequest("/supabase/documents/download", {
        id,
      });
      const url = response.signedUrl;
      const fileName = response.fileName || url.split("/").pop().split("?")[0];

      const fileResponse = await fetch(url);
      const blob = await fileResponse.blob();

      return { blob, fileName };
    } catch (err) {
      showError({
        title: "Download Failed",
        description: getErrorMessage(err),
      });
      return null;
    }
  };

  const downloadSelectedDocuments = async () => {
    const files = [];
    for (const id of selectedRows) {
      setDocumentDownloading(true);
      const file = await downloadDocument(id);
      if (file) files.push(file);
    }
    setDownloadFiles(files);
    setDocumentDownloading(false);
  };

  useEffect(() => {
    if (downloadFiles.length > 0) {
      downloadFiles.forEach(file => {
        const url = URL.createObjectURL(file.blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = file.fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      });
      setDownloadFiles([]);
    }
  }, [downloadFiles]);

  return (
    <>
      <PageHeader title="Documents" />
      <WidgetBox>
        <Flex flexDirection="column" gap={4}>
          <Flex gap={2} alignItems="center">
            <Flex gap={2} alignItems="center" flexGrow={1}>
              <InputSearch
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                width="300px"
              />
            </Flex>

            <Flex alignContent="center" gap={2}>
              {selectedRows.length > 0 && (
                <Button
                  variant="outline"
                  onClick={documentDownloading ? undefined : downloadSelectedDocuments}
                  isLoading={documentDownloading}
                  leftIcon={<FontAwesomeIcon icon={["fas", "download"]} />}
                >
                  Export
                </Button>
              )}
              <UploadDocuments />
            </Flex>
          </Flex>

          {isLoading ? (
            <Center h="80px">
              <Spinner />
            </Center>
          ) : (documents?.length ?? 0) === 0 ? (
            <NoResults message="No documents to display" />
          ) : (
            <DocumentsTable
              documents={filteredDocuments}
              columns={[
                "select",
                "Name",
                "Loan",
                "Type",
                "Uploader",
                "Status",
                "Uploaded",
                "Category",
              ]}
              selectedRows={selectedRows}
              onRowSelect={handleRowSelect}
              onAllRowSelect={handleSelectAllRows}
              selectAllChecked={selectAllChecked}
            />
          )}
        </Flex>
      </WidgetBox>
    </>
  );
}

export default Documents;
